import { useState } from 'react'
import { Box, Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { StatusChip } from '../../shared'
import { useGrantSessions, useRevokeSession } from '../../../hooks/useCampaign'
import type { GrantSessionRow } from '../../../types/canvasserGrant'

// L11 slice 1e, Task 17: device rows for a single canvasser grant, shown in the roster table's
// expand panel (see CanvasserRosterTable's `expandedRowKey`/`renderExpanded` wiring). One
// useRevokeSession mutation instance is shared across all device rows in this panel -- per-row
// pending/failed state is tracked locally in Set<sessionId> state (NOT read off the shared
// mutation object, and NOT a single scalar) so that revoking/retrying one device's session can
// never touch another device's pending spinner or failure signal. [Fix round 1] A single-scalar
// `failedSessionId` originally meant `handleRevoke(sessionId)` unconditionally cleared ANY
// standing failure -- revoking device B after device A's revoke had failed silently wiped A's
// still-live failure chip even though A was never retried. Per-session Sets close that hole.
//
// [LEGAL] Sec.E "surface, don't swallow": a failed server-side revoke must not vanish into a
// console.error or a toast that scrolls away -- it renders as a standing danger-idiom row with a
// retry affordance until the organizer either succeeds or navigates away.

interface CanvasserSessionRowsProps {
  campaignId: string
  grantId: string
}

// Dependency-free relative-time formatter, minute/hour/day granularity (the repo's
// lib/formatDate.ts `formatRelativeDate` buckets by CALENDAR DAY -- "Today"/"Yesterday" --
// which can't distinguish "2 minutes ago" from "11pm today"). `now` is injectable for
// deterministic tests.
export function formatRelativeTime(value: string, now: Date = new Date()): string {
  const diffMs = Math.max(0, now.getTime() - new Date(value).getTime())
  const diffMins = Math.floor(diffMs / 60_000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

function DeviceRow({
  session, onRevoke, isPending, hasFailed,
}: {
  session: GrantSessionRow
  onRevoke: (sessionId: string) => void
  isPending: boolean
  hasFailed: boolean
}) {
  return (
    <VStack align="stretch" gap={2} py={2} borderBottomWidth="1px" borderColor="border">
      <HStack justify="space-between" gap={3} flexWrap="wrap">
        <VStack align="start" gap={0}>
          <Text fontWeight="medium" color={session.revoked ? 'muted' : undefined}>
            {session.deviceLabel ?? 'Unknown device'}{session.revoked ? ' (signed out)' : ''}
          </Text>
          {!!session.clientInfo && (
            <Text fontSize="xs" color="muted">{session.clientInfo}</Text>
          )}
          <Text fontSize="xs" color="muted">
            Last seen {formatRelativeTime(session.lastSeenAt)}
          </Text>
        </VStack>
        {!session.revoked && (
          <Button
            size="sm"
            minH="44px"
            _touch={{ minH: '44px' }}
            variant="outline"
            loading={isPending}
            onClick={() => onRevoke(session.sessionId)}
          >
            Sign out this phone
          </Button>
        )}
      </HStack>
      {hasFailed && (
        <HStack gap={2} flexWrap="wrap">
          <StatusChip variant="danger">Server-side revoke FAILED — retry</StatusChip>
          <Button
            size="xs"
            minH="44px"
            _touch={{ minH: '44px' }}
            variant="outline"
            borderColor="danger.fg"
            color="danger.fg"
            loading={isPending}
            onClick={() => onRevoke(session.sessionId)}
          >
            Retry
          </Button>
        </HStack>
      )}
    </VStack>
  );
}

export function CanvasserSessionRows({ campaignId, grantId }: CanvasserSessionRowsProps) {
  const { data: sessions, isLoading } = useGrantSessions(campaignId, grantId)
  const revokeSession = useRevokeSession(campaignId)

  // Per-session (not per-panel) pending/failed sets -- see the [Fix round 1] note above. Every
  // update is via a functional `prev =>` updater that copies-and-mutates only `sessionId`'s own
  // membership, so a concurrent or later action on a DIFFERENT session's row can never remove
  // this session's entry.
  const [pendingSessionIds, setPendingSessionIds] = useState<Set<string>>(new Set())
  const [failedSessionIds, setFailedSessionIds] = useState<Set<string>>(new Set())

  const handleRevoke = async (sessionId: string) => {
    setPendingSessionIds((prev) => new Set(prev).add(sessionId))
    // Clear only THIS session's prior failure -- other sessions' failure chips are untouched.
    setFailedSessionIds((prev) => {
      if (!prev.has(sessionId)) return prev
      const next = new Set(prev)
      next.delete(sessionId)
      return next
    })
    try {
      await revokeSession.mutateAsync({ grantId, sessionId })
    } catch {
      setFailedSessionIds((prev) => new Set(prev).add(sessionId))
    } finally {
      setPendingSessionIds((prev) => {
        const next = new Set(prev)
        next.delete(sessionId)
        return next
      })
    }
  }

  if (isLoading) {
    return (
      <Box px={4} py={6} textAlign="center" bg="surface.2">
        <Spinner size="sm" color="accent" />
      </Box>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Box px={4} py={4} bg="surface.2">
        <Text fontSize="sm" color="muted">No devices yet.</Text>
      </Box>
    );
  }

  return (
    <Box px={4} py={2} bg="surface.2">
      <VStack align="stretch" gap={0}>
        {sessions.map((s) => (
          <DeviceRow
            key={s.sessionId}
            session={s}
            onRevoke={handleRevoke}
            isPending={pendingSessionIds.has(s.sessionId)}
            hasFailed={failedSessionIds.has(s.sessionId)}
          />
        ))}
      </VStack>
    </Box>
  );
}
